/* VDB Vehicle Lookup 1.7.1 — saved-vehicle lock + strict Shopify-native vehicle-only results filtering. */
(() => {
  'use strict';
  if (window.VDBMMY && window.VDBMMY.version === '1.7.1') return;

  const raw = window.VDBMMY_DATA;
  const ready = !!(raw && raw.schema === 1 && raw.catalog === 'UK26' && Array.isArray(raw.makes));
  const catalog = ready ? raw.catalog : 'UK26';
  const makeMap = new Map();
  const memory = new Map();
  let vehicleDataPromise = null;
  let versionGidPromise = null;
  const FITMENT_PARAM = 'filter.p.m.custom.compatible_vehicle_versions';

  if (ready) {
    raw.makes.forEach(make => {
      const models = new Map();
      make[3].forEach(model => models.set(model[0], {
        id: model[0], handle: model[1], name: model[2],
        years: model[3], yearSet: new Set(model[3])
      }));
      makeMap.set(make[0], {id: make[0], handle: make[1], name: make[2], models});
    });
  }

  const defaultShop = () => (window.Shopify && window.Shopify.shop) || location.host;
  const keyFor = shop => 'vdb:vehicle:' + catalog + ':' + shop;
  const legacyKeyFor = shop => 'vdb:mmy:' + catalog + ':' + shop;

  function baseNormalize(candidate) {
    if (!ready || !candidate || typeof candidate !== 'object') return null;
    const make = makeMap.get(candidate.makeId);
    const model = make && make.models.get(candidate.modelId);
    const year = Number(candidate.year);
    if (!model || !Number.isInteger(year) || !model.yearSet.has(year)) return null;
    return {
      makeId: make.id, makeHandle: make.handle, makeName: make.name,
      modelId: model.id, modelHandle: model.handle, modelName: model.name,
      year
    };
  }

  function normalizeStored(candidate) {
    const base = baseNormalize(candidate);
    if (!base || !candidate.engineKey || !candidate.engineLabel || !Array.isArray(candidate.versionIds) || !candidate.versionIds.length) return null;
    const versionIds = [...new Set(candidate.versionIds.filter(value => /^UK26-V-\d{6}$/.test(String(value))))];
    if (!versionIds.length) return null;
    return {
      schema: 2,
      catalog,
      ...base,
      engineKey: String(candidate.engineKey),
      engineName: String(candidate.engineName || candidate.engineLabel),
      engineLabel: String(candidate.engineLabel),
      engineShortLabel: String(candidate.engineShortLabel || candidate.engineName || candidate.engineLabel),
      versionGroupKey: String(candidate.versionGroupKey || ''),
      versionLabel: String(candidate.versionLabel || ''),
      versionIds,
      versionId: versionIds.length === 1 ? versionIds[0] : null,
      vehicleKey: String(candidate.vehicleKey || [base.modelId, base.year, candidate.engineKey, candidate.versionGroupKey || ''].join('|'))
    };
  }

  function getSelection(shop = defaultShop()) {
    if (!shop || !ready) return null;
    const key = keyFor(shop);
    let value;
    try { value = localStorage.getItem(key); }
    catch (_) { return normalizeStored(memory.get(key)); }
    if (value === null) return normalizeStored(memory.get(key));
    try {
      const stored = JSON.parse(value);
      return stored && stored.catalog === catalog ? normalizeStored(stored) : null;
    } catch (_) { return null; }
  }

  function getLegacyDraft(shop = defaultShop()) {
    if (!shop || !ready) return null;
    try {
      const value = localStorage.getItem(legacyKeyFor(shop));
      if (!value) return null;
      return baseNormalize(JSON.parse(value));
    } catch (_) { return null; }
  }

  function persist(shop, selection, action = 'save', source = null) {
    if (!shop) return false;
    const valid = selection ? normalizeStored(selection) : null;
    if (selection && !valid) return false;
    const key = keyFor(shop);
    if (valid) memory.set(key, valid); else memory.delete(key);
    try {
      if (valid) localStorage.setItem(key, JSON.stringify(valid));
      else localStorage.removeItem(key);
      localStorage.removeItem(legacyKeyFor(shop));
    } catch (_) { /* In-memory selection still works when browser storage is blocked. */ }
    const detail = {shop, selection: valid ? {...valid} : null, action, source};
    document.dispatchEvent(new CustomEvent('vdb:vehicle:change', {detail}));
    document.dispatchEvent(new CustomEvent('vdb:mmy:change', {detail}));
    return true;
  }

  function fill(select, entries, placeholder, chosen) {
    const fragment = document.createDocumentFragment();
    fragment.appendChild(new Option(placeholder, ''));
    entries.forEach(([value, label]) => fragment.appendChild(new Option(label, String(value))));
    select.replaceChildren(fragment);
    select.disabled = entries.length === 0;
    select.value = chosen !== null && chosen !== undefined && entries.some(e => String(e[0]) === String(chosen)) ? String(chosen) : '';
  }

  function loading(select, text) {
    select.replaceChildren(new Option(text, ''));
    select.disabled = true;
  }

  function modelNumber(modelId) {
    const match = String(modelId || '').match(/(\d+)$/);
    return match ? String(Number(match[1])) : '';
  }

  function versionId(number) {
    return 'UK26-V-' + String(number).padStart(6, '0');
  }

  function loadVehicleData() {
    const existing = window.VDBVEHICLE_DATA;
    if (existing && existing.schema === 2 && existing.catalog === catalog && existing.models && Array.isArray(existing.strings)) return Promise.resolve(existing);
    if (vehicleDataPromise) return vehicleDataPromise;
    const url = window.VDB_VEHICLE_INDEX_URL;
    if (!url) return Promise.reject(new Error('Vehicle engine data is not configured.'));
    vehicleDataPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.dataset.vdbVehicleIndex = 'true';
      script.onload = () => {
        const data = window.VDBVEHICLE_DATA;
        if (data && data.schema === 2 && data.catalog === catalog && data.models && Array.isArray(data.strings)) resolve(data);
        else reject(new Error('Vehicle engine data could not be validated.'));
      };
      script.onerror = () => reject(new Error('Vehicle engine data could not be loaded.'));
      document.head.appendChild(script);
    }).catch(error => {
      vehicleDataPromise = null;
      throw error;
    });
    return vehicleDataPromise;
  }


  function loadVersionGidData() {
    const existing = window.VDBVERSION_GIDS;
    if (existing && existing.schema === 1 && existing.catalog === catalog && Array.isArray(existing.ids)) return Promise.resolve(existing);
    if (versionGidPromise) return versionGidPromise;
    const url = window.VDB_VERSION_GID_INDEX_URL;
    if (!url) return Promise.reject(new Error('Shopify vehicle reference data is not configured.'));
    versionGidPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.dataset.vdbVersionGids = 'true';
      script.onload = () => {
        const data = window.VDBVERSION_GIDS;
        if (data && data.schema === 1 && data.catalog === catalog && Array.isArray(data.ids)) resolve(data);
        else reject(new Error('Shopify vehicle reference data could not be validated.'));
      };
      script.onerror = () => reject(new Error('Shopify vehicle reference data could not be loaded.'));
      document.head.appendChild(script);
    }).catch(error => {
      versionGidPromise = null;
      throw error;
    });
    return versionGidPromise;
  }

  function versionNumber(value) {
    const match = String(value || '').match(/^UK26-V-(\d{6})$/);
    return match ? Number(match[1]) : 0;
  }

  async function shopifyGidsForSelection(selection) {
    const valid = normalizeStored(selection);
    if (!valid) return [];
    const data = await loadVersionGidData();
    const gids = valid.versionIds.map(id => {
      const n = versionNumber(id);
      const shopifyId = n > 0 ? data.ids[n - 1] : null;
      return shopifyId ? 'gid://shopify/Metaobject/' + shopifyId : null;
    }).filter(Boolean);
    return [...new Set(gids)];
  }

  function currentFitmentValues(url = new URL(location.href)) {
    return url.searchParams.getAll(FITMENT_PARAM)
      .flatMap(value => String(value || '').split(','))
      .map(value => value.trim())
      .filter(Boolean)
      .sort();
  }

  function sameValues(left, right) {
    const a = [...new Set(left || [])].sort();
    const b = [...new Set(right || [])].sort();
    return a.length === b.length && a.every((value, index) => value === b[index]);
  }

  async function partsUrl(baseUrl, selection) {
    const gids = await shopifyGidsForSelection(selection);
    if (!gids.length) throw new Error('This vehicle could not be linked to Shopify.');
    const url = new URL(baseUrl || location.href, location.origin);
    url.searchParams.delete(FITMENT_PARAM);
    url.searchParams.set(FITMENT_PARAM, gids.join(','));
    url.searchParams.delete('page');
    return url.toString();
  }

  function withoutPartsFilter(baseUrl = location.href) {
    const url = new URL(baseUrl, location.origin);
    url.searchParams.delete(FITMENT_PARAM);
    url.searchParams.delete('page');
    return url.toString();
  }


  // Strict vehicle-results mode.
  // When a vehicle is saved, every Shopify collection and search results page
  // is filtered at source by Compatible Vehicle Versions. This preserves
  // Shopify pagination, counts, sorting and all normal customer filters.
  function isVehicleResultsPage(url = new URL(location.href)) {
    const path = url.pathname.replace(/\/+$/, '') || '/';
    return /(?:^|\/)collections\/[^/]+$/.test(path) || /(?:^|\/)search$/.test(path);
  }

  let globalFilterNavigation = false;

  async function enforceSavedVehicleResults(selection = getSelection()) {
    if (!isVehicleResultsPage() || globalFilterNavigation) return;
    const saved = normalizeStored(selection);
    if (!saved) return;
    try {
      const gids = await shopifyGidsForSelection(saved);
      if (!gids.length) return;
      const current = currentFitmentValues();
      if (sameValues(gids, current)) return;
      globalFilterNavigation = true;
      const target = await partsUrl(location.href, saved);
      location.replace(target);
    } catch (_) {
      globalFilterNavigation = false;
    }
  }

  function clearSavedVehicleResultsFilter() {
    if (!isVehicleResultsPage() || globalFilterNavigation) return;
    const url = new URL(location.href);
    if (!url.searchParams.has(FITMENT_PARAM)) return;
    globalFilterNavigation = true;
    location.replace(withoutPartsFilter(url.toString()));
  }

  function fuelFromVariant(value) {
    const s = String(value || '');
    if (/plug[- ]?in hybrid|phev/i.test(s)) return 'Plug-in Hybrid';
    if (/mild hybrid|mhev/i.test(s)) return 'Mild Hybrid';
    if (/electric motor|electric|elektro|bev/i.test(s)) return 'Electric';
    if (/diesel/i.test(s)) return 'Diesel';
    if (/hybrid/i.test(s)) return 'Hybrid';
    if (/petrol/i.test(s)) return 'Petrol';
    if (/\blpg\b/i.test(s)) return 'LPG';
    if (/\bcng\b/i.test(s)) return 'CNG';
    return '';
  }

  function cleanVariant(value) {
    return String(value || '')
      .replace(/^\s*(Plug[- ]?in Hybrid|Mild Hybrid|Electric Motor|Electric|Diesel|Petrol|Hybrid|LPG|CNG)\s+/i, '')
      .replace(/\s+/g, ' ').trim();
  }

  function cleanBodyStyle(value) {
    return String(value || '').replace(/\s*--\s*/g, ' ').replace(/\s+/g, ' ').trim();
  }

  function cleanSpec(value) {
    return String(value || '').replace(/^'+/, '').replace(/\s+/g, ' ').trim();
  }

  function engineLabels(type, spec, fuel) {
    const clean = cleanSpec(spec);
    const cc = (clean.match(/\b(\d+)cc\b/i) || [])[1];
    const kw = (clean.match(/\b(\d+(?:\.\d+)?)KW\b/i) || [])[1];
    const hp = (clean.match(/\b(\d+(?:\.\d+)?)HP\b/i) || [])[1];
    const codes = clean
      .replace(/\b\d+cc\b/ig, '')
      .replace(/\b\d+(?:\.\d+)?KW\b/ig, '')
      .replace(/\b\d+(?:\.\d+)?HP\b/ig, '')
      .replace(/^\s*--\s*$/, '')
      .trim()
      .replace(/;/g, '/');
    const details = [];
    if (fuel && !new RegExp(fuel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i').test(type)) details.push(fuel);
    if (cc) details.push(cc + 'cc');
    if (hp) details.push(hp + 'HP');
    if (kw) details.push(kw + 'kW');
    if (codes && codes !== '--') details.push(codes);
    const label = [type || 'Engine', details.length ? details.join(' · ') : clean].filter(Boolean).join(' — ');
    const short = [type || 'Engine', hp ? hp + 'HP' : '', fuel && !/diesel|petrol|hybrid|electric|lpg|cng/i.test(type) ? fuel : ''].filter(Boolean).join(' · ');
    return {label, short};
  }

  function engineKey(type, spec, fuel) {
    return JSON.stringify([String(type || ''), cleanSpec(spec), String(fuel || '')]);
  }

  function rowsFor(data, modelId, year) {
    const rows = data.models[modelNumber(modelId)] || [];
    return rows.filter(row => year >= Number(row[5]) && year <= Number(row[6])).map(row => ({
      versionId: versionId(row[0]),
      type: data.strings[row[1]] || '',
      spec: data.strings[row[2]] || '',
      variant: data.strings[row[3]] || '',
      bodyStyle: data.strings[row[4]] || '',
      yearFrom: Number(row[5]),
      yearTo: Number(row[6])
    }));
  }

  function groupEngines(rows) {
    const groups = new Map();
    rows.forEach(row => {
      const fuel = fuelFromVariant(row.variant);
      const key = engineKey(row.type, row.spec, fuel);
      if (!groups.has(key)) {
        const labels = engineLabels(row.type, row.spec, fuel);
        groups.set(key, {key, name: row.type || 'Engine', label: labels.label, shortLabel: labels.short, fuel, rows: []});
      }
      groups.get(key).rows.push(row);
    });
    return [...groups.values()].sort((a,b) => a.label.localeCompare(b.label, undefined, {numeric:true, sensitivity:'base'}));
  }

  function versionGroups(rows) {
    const groups = new Map();
    rows.forEach(row => {
      const body = cleanVariant(row.variant);
      const chassis = cleanBodyStyle(row.bodyStyle);
      const range = row.yearFrom === row.yearTo ? String(row.yearFrom) : row.yearFrom + '–' + row.yearTo;
      const label = [body, chassis, range].filter(Boolean).join(' · ') || 'Vehicle version';
      const key = JSON.stringify([body, chassis, row.yearFrom, row.yearTo]);
      if (!groups.has(key)) groups.set(key, {key, label, versionIds: []});
      groups.get(key).versionIds.push(row.versionId);
    });
    return [...groups.values()]
      .map(group => ({...group, versionIds:[...new Set(group.versionIds)].sort()}))
      .sort((a,b) => a.label.localeCompare(b.label, undefined, {numeric:true, sensitivity:'base'}));
  }

  function selectionSummary(selection, compact = false) {
    if (!selection) return '';
    const parts = [selection.makeName + ' ' + selection.modelName, String(selection.year), compact ? selection.engineShortLabel : selection.engineLabel];
    if (selection.versionLabel && selection.versionLabel !== 'Vehicle version') parts.push(selection.versionLabel);
    return parts.filter(Boolean).join(' · ');
  }

  class VDBMMY extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this.shop = this.dataset.shop || defaultShop();
      this.make = this.querySelector('[data-vdb-make]');
      this.model = this.querySelector('[data-vdb-model]');
      this.year = this.querySelector('[data-vdb-year]');
      this.engine = this.querySelector('[data-vdb-engine]');
      this.version = this.querySelector('[data-vdb-version]');
      this.versionField = this.querySelector('[data-vdb-version-field]');
      this.form = this.querySelector('[data-vdb-form]');
      this.button = this.querySelector('[data-vdb-save]');
      this.message = this.querySelector('[data-vdb-message]');
      this.current = this.querySelector('[data-vdb-current]');
      this.currentName = this.querySelector('[data-vdb-current-name]');
      this.partsButton = this.querySelector('[data-vdb-parts]');
      if (!this.make || !this.model || !this.year || !this.engine || !this.version || !this.versionField || !this.form || !this.button || !this.message || !this.current || !this.currentName) return;
      if (!ready) {
        this.message.textContent = 'Vehicle data could not be loaded. Please refresh the page.';
        this.button.disabled = true;
        return;
      }
      this._connected = true;
      this._abort = new AbortController();
      this._loadToken = 0;
      const signal = this._abort.signal;

      this.make.addEventListener('change', () => this.makeChanged(), {signal});
      this.model.addEventListener('change', () => this.modelChanged(), {signal});
      this.year.addEventListener('change', () => this.yearChanged(), {signal});
      this.engine.addEventListener('change', () => this.engineChanged(), {signal});
      this.version.addEventListener('change', () => this.updateSubmit(), {signal});
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        const selection = this.draftSelection();
        if (!selection) return;
        this.showSaved(selection);
        persist(this.shop, selection, 'save', this);
        this.message.textContent = 'Your vehicle is saved. Clear vehicle to select another.';
        this.refreshParts(selection);
        if (this.dataset.vdbAutoApply === 'true') this.goToParts(selection);
      }, {signal});
      this.querySelectorAll('[data-vdb-clear]').forEach(button => button.addEventListener('click', () => {
        this.render(null, null);
        persist(this.shop, null, 'clear', this);
        this.message.textContent = 'Vehicle selection cleared.';
        this.refreshParts(null);
        if (this.dataset.vdbAutoApply === 'true' && new URL(location.href).searchParams.has(FITMENT_PARAM)) {
          location.assign(withoutPartsFilter(location.href));
        }
      }, {signal}));
      if (this.partsButton) {
        this.partsButton.addEventListener('click', () => {
          const selection = this.draftSelection() || getSelection(this.shop);
          if (selection) this.goToParts(selection);
        }, {signal});
      }
      this._onChange = event => {
        if (!event.detail || event.detail.shop !== this.shop || event.detail.source === this) return;
        this.render(event.detail.selection, null);
        if (this.dataset.vdbAutoApply === 'true') {
          if (event.detail.action === 'save' && event.detail.selection) this.goToParts(event.detail.selection);
          if (event.detail.action === 'clear' && new URL(location.href).searchParams.has(FITMENT_PARAM)) {
            location.assign(withoutPartsFilter(location.href));
          }
        }
      };
      this._onStorage = event => {
        if (event.key === keyFor(this.shop) || event.key === legacyKeyFor(this.shop) || event.key === null) this.render(getSelection(this.shop), getLegacyDraft(this.shop));
      };
      document.addEventListener('vdb:vehicle:change', this._onChange, {signal});
      window.addEventListener('storage', this._onStorage, {signal});
      const initialSelection = getSelection(this.shop);
      this.render(initialSelection, getLegacyDraft(this.shop));
      if (this.dataset.vdbAutoFilter === 'true' && initialSelection) {
        setTimeout(() => this.ensurePartsFilter(initialSelection), 0);
      }
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
      this._connected = false;
    }

    render(selection, legacyDraft) {
      this._loadToken += 1;
      const saved = normalizeStored(selection);
      const draft = saved || baseNormalize(legacyDraft);
      fill(this.make, raw.makes.map(m => [m[0],m[2]]), 'Select make', draft && draft.makeId);
      const make = draft && makeMap.get(draft.makeId);
      fill(this.model, make ? [...make.models.values()].map(m => [m.id,m.name]) : [], 'Select model', draft && draft.modelId);
      const model = make && draft && make.models.get(draft.modelId);
      fill(this.year, model ? model.years.map(y => [y,String(y)]).reverse() : [], 'Select year', draft && draft.year);
      fill(this.engine, [], draft && draft.year ? 'Loading engines…' : 'Select year first', null);
      this.hideVersion();
      this.button.disabled = true;
      this.showSaved(saved);
      this.refreshParts(saved);
      this.message.textContent = saved ? 'Your vehicle is saved. Clear vehicle to select another.' : (draft ? 'Choose the engine to complete your vehicle selection.' : '');
      if (draft && draft.year) this.loadEngines(saved && saved.engineKey, saved && saved.versionGroupKey, saved && saved.versionIds);
    }

    showSaved(saved) {
      this.saved = normalizeStored(saved);
      this.current.hidden = !this.saved;
      this.currentName.textContent = this.saved ? selectionSummary(this.saved, false) : '';
      this.applySavedLock();
    }

    applySavedLock() {
      const locked = !!this.saved;
      this.dataset.vdbLocked = locked ? 'true' : 'false';
      [this.make, this.model, this.year, this.engine, this.version].forEach(select => {
        if (locked) {
          select.disabled = true;
          select.setAttribute('aria-disabled', 'true');
        } else {
          select.removeAttribute('aria-disabled');
        }
      });
      if (locked) {
        this.button.disabled = true;
        this.button.setAttribute('aria-disabled', 'true');
      } else {
        this.button.removeAttribute('aria-disabled');
      }
    }

    refreshParts(selection) {
      if (!this.partsButton) return;
      const saved = normalizeStored(selection);
      this.partsButton.hidden = !saved;
      this.partsButton.disabled = !saved;
    }

    async goToParts(selection) {
      const saved = normalizeStored(selection);
      if (!saved || this._navigatingToParts) return;
      this._navigatingToParts = true;
      if (this.partsButton) {
        this.partsButton.disabled = true;
        this.partsButton.dataset.loading = 'true';
      }
      const previousMessage = this.message.textContent;
      this.message.textContent = 'Loading parts for your vehicle…';
      try {
        const baseUrl = this.dataset.vdbPartsUrl === 'current' ? location.href : (this.dataset.vdbPartsUrl || location.href);
        const target = await partsUrl(baseUrl, saved);
        if (target === location.href) {
          this.message.textContent = 'Showing parts for your saved vehicle.';
          this._navigatingToParts = false;
          if (this.partsButton) {
            this.partsButton.disabled = false;
            delete this.partsButton.dataset.loading;
          }
          return;
        }
        location.assign(target);
      } catch (error) {
        this._navigatingToParts = false;
        if (this.partsButton) {
          this.partsButton.disabled = false;
          delete this.partsButton.dataset.loading;
        }
        this.message.textContent = error.message || previousMessage || 'Vehicle parts could not be loaded.';
      }
    }

    async ensurePartsFilter(selection) {
      const saved = normalizeStored(selection);
      if (!saved || this._checkingPartsFilter) return;
      this._checkingPartsFilter = true;
      try {
        const gids = await shopifyGidsForSelection(saved);
        const current = currentFitmentValues();
        if (gids.length && !sameValues(gids, current)) {
          const target = await partsUrl(location.href, saved);
          location.replace(target);
        }
      } catch (_) {
        // Keep the collection usable if Shopify reference data is unavailable.
      } finally {
        this._checkingPartsFilter = false;
      }
    }

    makeChanged(modelId = null, year = null) {
      this._loadToken += 1;
      const make = makeMap.get(this.make.value);
      fill(this.model, make ? [...make.models.values()].map(m => [m.id,m.name]) : [], 'Select model', typeof modelId === 'string' ? modelId : null);
      this.modelChanged(typeof year === 'number' ? year : null);
      this.message.textContent = '';
    }

    modelChanged(year = null) {
      this._loadToken += 1;
      const make = makeMap.get(this.make.value);
      const model = make && make.models.get(this.model.value);
      fill(this.year, model ? model.years.map(y => [y,String(y)]).reverse() : [], 'Select year', typeof year === 'number' ? year : null);
      fill(this.engine, [], 'Select year first', null);
      this.hideVersion();
      this.button.disabled = true;
      this.message.textContent = '';
    }

    yearChanged() {
      this._loadToken += 1;
      fill(this.engine, [], this.year.value ? 'Loading engines…' : 'Select year first', null);
      this.hideVersion();
      this.button.disabled = true;
      this.message.textContent = '';
      if (this.year.value) this.loadEngines();
    }

    async loadEngines(chosenEngineKey = null, chosenVersionKey = null, chosenVersionIds = null) {
      const token = ++this._loadToken;
      loading(this.engine, 'Loading engines…');
      this.hideVersion();
      this.button.disabled = true;
      try {
        const data = await loadVehicleData();
        if (token !== this._loadToken) return;
        const year = Number(this.year.value);
        const rows = rowsFor(data, this.model.value, year);
        this.engineGroups = new Map(groupEngines(rows).map(group => [group.key, group]));
        const entries = [...this.engineGroups.values()].map(group => [group.key, group.label]);
        fill(this.engine, entries, entries.length ? 'Select engine' : 'No engines found', chosenEngineKey);
        if (!entries.length) {
          this.message.textContent = 'No active vehicle versions were found for this year.';
          return;
        }
        if (this.engine.value) this.engineChanged(chosenVersionKey, chosenVersionIds);
      } catch (error) {
        if (token !== this._loadToken) return;
        loading(this.engine, 'Engine data unavailable');
        this.message.textContent = error.message || 'Vehicle engine data could not be loaded.';
      }
    }

    engineChanged(chosenVersionKey = null, chosenVersionIds = null) {
      this.hideVersion();
      this.button.disabled = true;
      const engine = this.engineGroups && this.engineGroups.get(this.engine.value);
      if (!engine) return;
      this.currentEngine = engine;
      const groups = versionGroups(engine.rows);
      this.versionGroups = new Map(groups.map(group => [group.key, group]));
      if (groups.length === 1) {
        this.selectedVersionGroup = groups[0];
        this.versionField.hidden = true;
        this.version.disabled = true;
      } else {
        this.versionField.hidden = false;
        let selectedKey = chosenVersionKey;
        if ((!selectedKey || !this.versionGroups.has(selectedKey)) && Array.isArray(chosenVersionIds) && chosenVersionIds.length) {
          const wanted = [...chosenVersionIds].sort().join('|');
          const matched = groups.find(group => group.versionIds.join('|') === wanted);
          selectedKey = matched ? matched.key : null;
        }
        fill(this.version, groups.map(group => [group.key, group.label]), 'Select version', selectedKey);
        this.selectedVersionGroup = this.version.value ? this.versionGroups.get(this.version.value) : null;
      }
      this.updateSubmit();
      this.applySavedLock();
    }

    hideVersion() {
      this.currentEngine = null;
      this.versionGroups = new Map();
      this.selectedVersionGroup = null;
      this.versionField.hidden = true;
      fill(this.version, [], 'Select version', null);
      this.version.disabled = true;
    }

    updateSubmit() {
      if (this.saved) {
        this.button.disabled = true;
        this.applySavedLock();
        return;
      }
      const base = baseNormalize({makeId:this.make.value, modelId:this.model.value, year:this.year.value});
      const engine = this.engineGroups && this.engineGroups.get(this.engine.value);
      if (!base || !engine) {
        this.button.disabled = true;
        return;
      }
      if (!this.versionField.hidden) this.selectedVersionGroup = this.versionGroups.get(this.version.value) || null;
      this.button.disabled = !this.selectedVersionGroup;
      this.message.textContent = '';
    }

    draftSelection() {
      const base = baseNormalize({makeId:this.make.value, modelId:this.model.value, year:this.year.value});
      const engine = this.engineGroups && this.engineGroups.get(this.engine.value);
      if (!base || !engine) return null;
      const group = this.versionField.hidden ? this.selectedVersionGroup : this.versionGroups.get(this.version.value);
      if (!group || !group.versionIds.length) return null;
      const ids = [...new Set(group.versionIds)].sort();
      return normalizeStored({
        schema:2, catalog, ...base,
        engineKey:engine.key,
        engineName:engine.name,
        engineLabel:engine.label,
        engineShortLabel:engine.shortLabel,
        versionGroupKey:group.key,
        versionLabel:groupsafeLabel(group, this.versionField.hidden),
        versionIds:ids,
        versionId:ids.length === 1 ? ids[0] : null,
        vehicleKey:[base.modelId, base.year, engine.key, group.key].join('|')
      });
    }
  }

  function groupsafeLabel(group, hidden) {
    if (!group) return '';
    return hidden ? '' : group.label;
  }

  if (!customElements.get('vdb-mmy')) customElements.define('vdb-mmy', VDBMMY);

  const api = Object.freeze({
    version:'1.7.1', catalog, ready,
    getSelection,
    clearSelection(shop = defaultShop()) { return persist(shop, null, 'clear'); },
    select(shop, selection) { return persist(shop, selection, 'save'); },
    loadVehicleData,
    loadVersionGidData,
    shopifyGidsForSelection,
    partsUrl,
    fitmentParam: FITMENT_PARAM,
    selectionSummary,
    normalize: normalizeStored
  });
  window.VDBMMY = api;
  window.VDBVehicle = api;

  // Apply the saved vehicle to every collection/search result page, including
  // searches reached from the site search box and collections opened later.
  const enforceInitialVehicleResults = () => {
    const saved = getSelection();
    if (saved) enforceSavedVehicleResults(saved);
    else clearSavedVehicleResultsFilter();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enforceInitialVehicleResults, {once:true});
  } else {
    setTimeout(enforceInitialVehicleResults, 0);
  }

  document.addEventListener('vdb:vehicle:change', event => {
    if (!event.detail || event.detail.shop !== defaultShop()) return;
    if (event.detail.selection) enforceSavedVehicleResults(event.detail.selection);
    else clearSavedVehicleResultsFilter();
  });

  window.addEventListener('pageshow', () => {
    if (isVehicleResultsPage()) setTimeout(enforceInitialVehicleResults, 0);
  });

  class VDBMMYBar extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this.shop = this.dataset.shop || defaultShop();
      this.trigger = this.querySelector('[data-vdb-open]');
      this.dialog = this.querySelector('[data-vdb-dialog]');
      this.name = this.querySelector('[data-vdb-bar-name]');
      this.action = this.querySelector('[data-vdb-bar-action]');
      if (!this.trigger || !this.dialog || !this.name || !this.action) return;
      this._connected = true;
      this._abort = new AbortController();
      const signal = this._abort.signal;
      this.trigger.addEventListener('click', () => this.open(), {signal});
      this.querySelectorAll('[data-vdb-close]').forEach(button => button.addEventListener('click', () => this.close(), {signal}));
      this.dialog.addEventListener('close', () => this.closed(), {signal});
      this.dialog.addEventListener('cancel', () => this.closed(), {signal});
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.close(); }, {signal});
      this._onChange = event => {
        if (!event.detail || event.detail.shop !== this.shop) return;
        this.refresh(event.detail.selection);
        if (event.detail.action === 'save' && event.detail.source && this.dialog.contains(event.detail.source)) this.close();
      };
      this._onStorage = event => { if (event.key === keyFor(this.shop) || event.key === null) this.refresh(getSelection(this.shop)); };
      document.addEventListener('vdb:vehicle:change', this._onChange, {signal});
      window.addEventListener('storage', this._onStorage, {signal});
      this.refresh(getSelection(this.shop));
      if (!ready || typeof this.dialog.showModal !== 'function') {
        this.trigger.disabled = true;
        this.name.textContent = 'Vehicle lookup unavailable';
      }
    }
    disconnectedCallback() {
      if (this._abort) this._abort.abort();
      if (this.dialog && this.dialog.open) this.dialog.close();
      this._connected = false;
    }
    refresh(selection) {
      const saved = normalizeStored(selection);
      this.name.textContent = saved ? selectionSummary(saved, true) : 'Choose your vehicle';
      this.action.textContent = saved ? 'Change' : 'Choose vehicle';
      this.trigger.setAttribute('aria-label', saved ? 'Change vehicle: ' + this.name.textContent : 'Choose your vehicle');
    }
    open() {
      if (!ready || this.dialog.open || typeof this.dialog.showModal !== 'function') return;
      this.dialog.showModal();
      this.trigger.setAttribute('aria-expanded','true');
      const first = this.dialog.querySelector('[data-vdb-make]');
      if (first) first.focus();
    }
    close() { if (this.dialog.open) this.dialog.close(); }
    closed() {
      this.trigger.setAttribute('aria-expanded','false');
      if (this.isConnected) this.trigger.focus({preventScroll:true});
    }
  }
  if (!customElements.get('vdb-mmy-bar')) customElements.define('vdb-mmy-bar', VDBMMYBar);
})();
